'use client';

import { useState, useTransition } from 'react';
import type { AxiosError } from '@cxnpl/api/axios';
import { Stack, YStack, H1, SchemaForm, FormField, useForm, Button, Text } from '@cxnpl/ui';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import type { BRAND } from 'zod';
import branding from '@cxnpl/ui/brand';
import { useVerifyEmail, useConfirmEmail, useCreateSignupConsents } from '@cxnpl/api/users/users';
import { useAppDispatch, useAppSelector } from 'app/store';
import i18n from 'app/i18n/i18n.config';
import {
  setEmailAddress,
  setMarketingAndOffersStatus,
  setPassword,
  setSignupId,
  selectAuthDetails,
} from 'app/features/landing/signup/signupSlice';
import { PageBackNavigationFrame } from 'app/components/PageBackNavigationFrame';
import { withTrackScreenView } from 'app/analytics';
import type { MfaFormProps } from 'app/provider/StepUpAuth/MfaForm';
import { MfaForm } from 'app/provider/StepUpAuth/MfaForm';
import { useSetActiveStep } from '../SignupFormContext';
import { passwordValidation } from './utils/passwordValidation';

export const EmailFormSchema = z.object({
  emailAddress: FormField.Text({
    required_error: `${i18n.t('auth.signUpPage.emailAddressRequiredErrorMessage')}`,
  })
    .describe(`${i18n.t('auth.signUpPage.emailAddressInputLabel')}`)
    .min(1, `${i18n.t('auth.signUpPage.emailAddressRequiredErrorMessage')}`)
    .email(`${i18n.t('auth.signUpPage.emailAddressValidErrorMessage')}`),

  password: FormField.Password((zString) =>
    zString({
      required_error: `${i18n.t('auth.components.loginForm.passwordRequiredErrorMessage')}`,
    }).min(8, `${i18n.t('auth.signUpPage.passwordInvalidLengthError')}`)
  )
    .describe(`${i18n.t('auth.signUpPage.createPasswordInputLabel')}`)
    .refine(passwordValidation, {
      message: i18n.t('auth.signUpPage.passwordRequirements'),
    }),
  marketingAndOffersStatus: FormField.BooleanCheckbox({
    required_error: 'Please agree to this before continuing',
  }).describe(`${i18n.t('auth.signUpPage.marketingAndOffers')}`),
});

const MfaFormWithAnalytics = withTrackScreenView(MfaForm, {
  pageName: 'EmailVerification',
});

function EmailForm({
  showMFAForm,
  setShowMFAForm,
}: {
  showMFAForm: boolean;
  setShowMFAForm: (value: boolean) => void;
}) {
  const { t } = useTranslation();
  const form = useForm(EmailFormSchema);
  const dispatch = useAppDispatch();
  const email = useVerifyEmail();
  const confirmEmail = useConfirmEmail();
  const createAcknowledgement = useCreateSignupConsents();
  const { emailAddress, termsAndConditionsChecked, password, marketingAndOffersStatus } =
    useAppSelector(selectAuthDetails);

  const [signupRecordId, setSignupRecordId] = useState<string>('');

  const [isPending, startTransition] = useTransition();
  const setActiveStep = useSetActiveStep();

  const onSubmit = async () => {
    dispatch(setEmailAddress(form.watch('emailAddress')));
    dispatch(setPassword(form.watch('password')));
    dispatch(setMarketingAndOffersStatus(form.watch('marketingAndOffersStatus')));

    try {
      const response = await email.mutateAsync({
        data: {
          email: form.watch('emailAddress'),
        },
      });

      if ('signupRecordId' in response) {
        startTransition(() => {
          setShowMFAForm(true);
          setSignupRecordId(response.signupRecordId);
          dispatch(setSignupId(response.signupRecordId));
        });

        await createAcknowledgement.mutateAsync({
          data: {
            // TODO: https://constantinople.atlassian.net/browse/WAC-22
            appVersion: 'latest-app-version-123467890',
            signupId: response.signupRecordId,
            aac: {
              APPLICATION_TERMS_CONDITIONS: termsAndConditionsChecked,
              PRIVACY_STATEMENT: termsAndConditionsChecked,
              MARKETING_CONSENT_APPLICATION: form.watch('marketingAndOffersStatus'),
              MARKETING_CONSENT_EMAIL: form.watch('marketingAndOffersStatus'),
              MARKETING_CONSENT_SMS: form.watch('marketingAndOffersStatus'),
            },
          },
        });
      }
    } catch (e) {
      const error = e as AxiosError;
      const errorMessage = error.response?.data.message
        ? error.response.data.message
        : t('auth.signUpPage.unknownError');

      form.setError('emailAddress', { message: errorMessage, type: 'value' });
    }
  };

  const onSubmitMFA: MfaFormProps['onSubmit'] = async ({ code }, mfaForm) => {
    try {
      const response = await confirmEmail.mutateAsync({
        data: {
          signupId: signupRecordId,
          code,
        },
      });

      // Handle invalid code
      if (!response.success) {
        let errorMessage;
        if (response.userIdAlreadyExists) {
          errorMessage = t('auth.components.mfaForm.emailAddressRegisteredMessage');
        } else if (response.attemptsExceeded) {
          errorMessage = t('auth.components.mfaForm.maxAttemptsMessage');
        } else {
          errorMessage = t('auth.components.mfaForm.invalidCodeResendMessage');
        }

        mfaForm.setValue('code', '' as string & BRAND<'passwordCode'>);
        mfaForm.setError('code', {
          message: errorMessage,
          type: 'value',
        });
        return;
      }

      startTransition(() => {
        setActiveStep('mobileNumber');
      });
    } catch (e) {
      const error = e as AxiosError;
      mfaForm.setValue('code', '' as string & BRAND<'passwordCode'>);
      mfaForm.setError('code', {
        message: error.response?.data.message || 'Invalid code',
        type: 'value',
      });
    }
  };

  const onResend: MfaFormProps['onResend'] = async (mfaForm) => {
    mfaForm.startCountdown(30);
    try {
      const response = await email.mutateAsync({
        data: {
          email: form.watch('emailAddress'),
        },
      });

      if ('signupRecordId' in response) {
        setSignupRecordId(response.signupRecordId);
      }
    } catch (e) {
      mfaForm.setError('code', {
        message: 'Failed to resend security code',
        type: 'value',
      });
    }
  };

  const { isSubmitting: isFormSubmitting } = form.formState;
  const isSubmitting = isFormSubmitting || isPending;

  return (
    <YStack
      width="100%"
      $tablet={{
        maxWidth: 536,
      }}
      $laptop={{
        maxWidth: 468,
      }}
    >
      {showMFAForm ? (
        <MfaFormWithAnalytics
          title={t('auth.components.mfaForm.title')}
          description={t('auth.components.mfaForm.emailDescription', { email: form.watch('emailAddress') })}
          onSubmit={onSubmitMFA}
          onResend={onResend}
          disabled={isPending}
        />
      ) : (
        <YStack width="100%" flexDirection="column">
          <H1 textAlign="left" color="$foreground/surface-default" marginBottom="$space.xl">
            {t('auth.signUpPage.title', { productName: branding.productName })}
          </H1>
          <Text variant="bodyMediumEm">{t('auth.signUpPage.emailAddressMessage')}</Text>
          <YStack>
            <SchemaForm
              form={form}
              formProps={{ disabled: isSubmitting }}
              props={{
                emailAddress: {
                  autoFocus: true,
                },
              }}
              onSubmit={onSubmit}
              schema={EmailFormSchema}
              defaultValues={{
                emailAddress,
                password,
                marketingAndOffersStatus,
              }}
            >
              {(fields) => (
                <YStack marginTop="$space.lg">
                  <YStack gap="$space.lg">
                    <Stack>{fields.emailAddress}</Stack>
                    <Stack>{fields.password}</Stack>
                  </YStack>
                  {!form.formState.errors.password && (
                    <YStack marginTop="$space.xs">
                      <Text variant="bodySmall" color="$foreground/app-subdued">
                        {t('auth.signUpPage.passwordValidationMessage')}
                      </Text>
                    </YStack>
                  )}
                  <Stack marginVertical="$space.2xl">{fields.marketingAndOffersStatus}</Stack>
                </YStack>
              )}
            </SchemaForm>
          </YStack>
          <YStack alignItems="center" flexDirection="row">
            <Button
              onPress={() => {
                void form.handleSubmit(onSubmit)();
              }}
              mode="primary"
              variant="filled"
              size="lg"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {t('common.buttons.continue')}
            </Button>
          </YStack>
        </YStack>
      )}
    </YStack>
  );
}

export function EmailStep() {
  const setActiveStep = useSetActiveStep();
  const [showMFAForm, setShowMFAForm] = useState<boolean>(false);

  return (
    <PageBackNavigationFrame
      onPressBack={() => {
        if (showMFAForm) {
          setShowMFAForm(false);
        } else {
          setActiveStep('signup');
        }
      }}
    >
      <EmailForm showMFAForm={showMFAForm} setShowMFAForm={setShowMFAForm} />
    </PageBackNavigationFrame>
  );
}

export const EmailStepWithAnalytics = withTrackScreenView(EmailStep, {
  pageName: 'EmailPassword',
});
